import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Control,
  Label,
  Select,
  SelectOption,
  PlusIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { connectionsApi } from '@/api/connections';
import type { CreateConnectionPayload } from '@/api/connections';

interface CreateConnectionDialogProps {
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateConnectionDialog = ({ partnerId, onClose, onSuccess }: CreateConnectionDialogProps) => {
  const [label, setLabel] = useState('');
  const [direction, setDirection] = useState<CreateConnectionPayload['direction']>('outbound');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('');
  const [authType, setAuthType] = useState<CreateConnectionPayload['authType']>('ssh_key');
  const [vaultSecretPath, setVaultSecretPath] = useState('');
  const [remoteBasePath, setRemoteBasePath] = useState('/');

  const { mutate: createConnection, isSubmitting } = useApiMutation<CreateConnectionPayload, void>({
    mutationFn: async (data) => {
      await connectionsApi.create(data);
    },
    successMessage: 'Connection created successfully',
    errorMessage: 'Failed to create connection',
    onSuccess: () => onSuccess(),
  });

  const isValid = label.trim() && host.trim() && username.trim() && vaultSecretPath.trim();

  const handleSubmit = async () => {
    if (!isValid) return;
    await createConnection({
      partnerId,
      label: label.trim(),
      direction,
      host: host.trim(),
      port: port ? parseInt(port, 10) : undefined,
      username: username.trim(),
      authType,
      vaultSecretPath: vaultSecretPath.trim(),
      remoteBasePath: remoteBasePath.trim() || undefined,
    });
  };

  return (
    <Modal
      title="Add Connection"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Add Connection'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Control hideSpacing="bottom">
            <Label htmlFor="conn-label">Label</Label>
            <Input
              id="conn-label"
              placeholder="e.g., sca1-export"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="conn-direction">Direction</Label>
            <Select
              id="conn-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as CreateConnectionPayload['direction'])}
            >
              <SelectOption value="outbound" label="Outbound (we push files)">
                Outbound (we push files)
              </SelectOption>
              <SelectOption value="inbound" label="Inbound (we pull files)">
                Inbound (we pull files)
              </SelectOption>
              <SelectOption value="both" label="Both">
                Both
              </SelectOption>
            </Select>
          </Control>

          <div className="flex gap-3">
            <Control hideSpacing="bottom" className="flex-1">
              <Label htmlFor="conn-host">Host</Label>
              <Input
                id="conn-host"
                placeholder="e.g., sftp.partner.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                required
              />
            </Control>
            <Control hideSpacing="bottom" className="w-24">
              <Label htmlFor="conn-port">Port</Label>
              <Input
                id="conn-port"
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </Control>
          </div>

          <Control hideSpacing="bottom">
            <Label htmlFor="conn-username">Username</Label>
            <Input
              id="conn-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="conn-auth-type">Auth Type</Label>
            <Select
              id="conn-auth-type"
              value={authType}
              onChange={(e) => setAuthType(e.target.value as CreateConnectionPayload['authType'])}
            >
              <SelectOption value="ssh_key" label="SSH key">
                SSH key
              </SelectOption>
              <SelectOption value="password" label="Password">
                Password
              </SelectOption>
            </Select>
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="conn-vault-path">Vault Secret Path</Label>
            <Input
              id="conn-vault-path"
              placeholder="e.g., sftp-connections/sca1/export"
              value={vaultSecretPath}
              onChange={(e) => setVaultSecretPath(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="conn-remote-path">Remote Base Path (Optional)</Label>
            <Input
              id="conn-remote-path"
              value={remoteBasePath}
              onChange={(e) => setRemoteBasePath(e.target.value)}
            />
          </Control>

          <p className="text-xs text-color-tertiary">
            Credentials are never entered here — this only records the Vault path where the SSH
            key or password already lives. Provision the secret in Vault first.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default CreateConnectionDialog;
